// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

/**
 * Friendly wording for a failed provider HTTP exchange. A user never sees a
 * raw HTTP status or response body (BUG-257, BUG-261); both stay in logs.
 */
export const ProviderHttpFailureKind = {
    UNAUTHORIZED: 'UNAUTHORIZED',
    NOT_FOUND: 'NOT_FOUND',
    RATE_LIMITED: 'RATE_LIMITED',
    PROVIDER_ERROR: 'PROVIDER_ERROR',
    REJECTED: 'REJECTED',
} as const;

export type ProviderHttpFailureKind =
    (typeof ProviderHttpFailureKind)[keyof typeof ProviderHttpFailureKind];

export function classifyProviderHttpFailure(httpStatus: number): ProviderHttpFailureKind {
    if (httpStatus === 401 || httpStatus === 403) return ProviderHttpFailureKind.UNAUTHORIZED;
    if (httpStatus === 404) return ProviderHttpFailureKind.NOT_FOUND;
    if (httpStatus === 429) return ProviderHttpFailureKind.RATE_LIMITED;
    if (httpStatus >= 500) return ProviderHttpFailureKind.PROVIDER_ERROR;
    return ProviderHttpFailureKind.REJECTED;
}

/**
 * A `READ` asks the provider something and may simply be retried. A `PAYMENT`
 * may have moved money, so its failure never invites a blind retry.
 */
export type ProviderOperationEffect = 'READ' | 'PAYMENT';

export interface ProviderHttpFailureDescription {
    /** Display name, for example "Clover", "CardPointe" or "Valor". */
    readonly provider: string;
    readonly httpStatus: number;
    /** What was asked of the provider, for example "this refund". */
    readonly subject: string;
    readonly effect?: ProviderOperationEffect;
    /** Replaces the default advice for rejected credentials. */
    readonly unauthorizedMessage?: string;
    /** Replaces the default advice when the provider finds nothing. */
    readonly notFoundMessage?: string;
}

const PAYMENT_OUTCOME_ADVICE =
    'Check the transaction in PILLAR before trying again, so it is not charged twice.';

export function describeProviderHttpFailure(input: ProviderHttpFailureDescription): string {
    const { provider, subject } = input;
    const payment = input.effect === 'PAYMENT';
    switch (classifyProviderHttpFailure(input.httpStatus)) {
        case ProviderHttpFailureKind.UNAUTHORIZED:
            return (
                input.unauthorizedMessage ??
                `${provider} did not accept Anchorbase's credentials. Ask an administrator to check the ${provider} credentials in PILLAR.`
            );
        case ProviderHttpFailureKind.NOT_FOUND:
            return (
                input.notFoundMessage ??
                `${provider} could not find ${subject}. Check that the ${provider} account and terminal settings are correct.`
            );
        case ProviderHttpFailureKind.RATE_LIMITED:
            return payment
                ? `${provider} is temporarily limiting requests and did not process ${subject}; try again in a minute.`
                : `${provider} is temporarily limiting requests; try again in a minute.`;
        case ProviderHttpFailureKind.PROVIDER_ERROR:
            return payment
                ? `${provider} had a problem while handling ${subject}. ${PAYMENT_OUTCOME_ADVICE}`
                : `${provider} is having a problem right now; try again in a few minutes.`;
        case ProviderHttpFailureKind.REJECTED:
            return `${provider} refused the request for ${subject}. If this continues, contact Anchorbase support.`;
    }
}

export function describeProviderTransportFailure(input: {
    readonly provider: string;
    readonly subject: string;
    readonly timedOut: boolean;
    readonly effect?: ProviderOperationEffect;
}): string {
    if (input.effect === 'PAYMENT') {
        return input.timedOut
            ? `${input.provider} did not answer in time about ${input.subject}. ${PAYMENT_OUTCOME_ADVICE}`
            : `Anchorbase could not reach ${input.provider} about ${input.subject}. ${PAYMENT_OUTCOME_ADVICE}`;
    }
    return input.timedOut
        ? `${input.provider} did not answer in time about ${input.subject}; try again.`
        : `Anchorbase could not reach ${input.provider} about ${input.subject}; check the network connection and try again.`;
}

/** Seconds from a numeric `Retry-After` header; an HTTP date or absent header yields null. */
export function readRetryAfterSeconds(response: Response): number | null {
    const header = response.headers.get('retry-after');
    if (header === null || !/^\s*\d+\s*$/.test(header)) return null;
    return Number.parseInt(header, 10);
}

const MAXIMUM_PROVIDER_MESSAGE_LENGTH = 200;
const RAW_PROVIDER_TEXT_PATTERNS: readonly RegExp[] = [
    // An HTTP status, with or without its reason phrase.
    /\bHTTP\b/iu,
    /\b(?:status(?:\s*code)?|error\s*code|code)\s*[:=#]?\s*[1-5]\d{2}\b/iu,
    /\b[1-5]\d{2}\s+(?:OK|Bad|Unauthori[sz]ed|Forbidden|Not\s+Found|Method|Request|Conflict|Too\s+Many|Internal|Service|Gateway)\b/iu,
    // An HTML or XML body.
    /<\s*\/?\s*[a-z!?][^>]*>/iu,
    // A JSON body.
    /^\s*[[{]/u,
    /"[^"]+"\s*:/u,
    // A stack trace.
    /\n\s*at\s+\S/u,
    /\bat\s+\S+\s+\([^)]*:\d+:\d+\)/u,
];

/**
 * Provider text a user may see, such as a decline reason. Text that carries
 * an HTTP status, a response body or a stack trace is rejected, so the caller
 * falls back to the friendly wording (BUG-261).
 */
export function sanitizeProviderMessage(text: string | null | undefined): string | undefined {
    const normalized = (text ?? '').replace(/\s+/gu, ' ').trim();
    if (normalized.length === 0 || normalized.length > MAXIMUM_PROVIDER_MESSAGE_LENGTH) {
        return undefined;
    }
    const raw = text ?? '';
    return RAW_PROVIDER_TEXT_PATTERNS.some(
        (pattern) => pattern.test(raw) || pattern.test(normalized)
    )
        ? undefined
        : normalized;
}

/**
 * Provider text for a user, such as a decline reason, or a friendly fallback
 * when the text is missing or looks raw (BUG-261).
 */
export function describeProviderText(input: {
    readonly provider: string;
    readonly text: string | null | undefined;
    readonly subject: string;
    readonly effect?: ProviderOperationEffect;
}): string {
    return (
        sanitizeProviderMessage(input.text) ??
        (input.effect === 'PAYMENT'
            ? `${input.provider} did not complete ${input.subject}. ${PAYMENT_OUTCOME_ADVICE}`
            : `${input.provider} did not complete ${input.subject}. Try again in a few minutes.`)
    );
}
